import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import AuthCard from "../../../components/auth/AuthCard";
import CircularProgress from "../../../components/charts/CircularProgress";
import TodoCreateModal from "./components/TodoCreateModal";
import TodoEditModal from "./components/TodoEditModal";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import BottomNav from "../../../components/common/BottomNav";
import { useAddTodo, useDeleteTodo, useTodos, useToggleTodo, useUpdateTodo } from "../../../hooks/useTodos";
import { useNavigate } from "react-router-dom";

export default function TodoList() {
    const queryClient = useQueryClient();
    const token = localStorage.getItem("token");
    const { data: tasks = [], isLoading, isError, error } = useTodos();
    const addMutation = useAddTodo();
    const toggleMutation = useToggleTodo();
    const updateMutation = useUpdateTodo();
    const deleteMutation = useDeleteTodo();
    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    const ready = useLoadingGate();
    const navigate = useNavigate();
    const completed = useMemo(() => tasks.filter((t) => t.done).length, [tasks]);
    const percentInt = useMemo(
        () => (tasks.length ? Math.round((completed / tasks.length) * 100) : 0),
        [tasks, completed]
    );
    const doneText = `${completed}/${tasks.length}개 실천 완료`;

    const handleAdd = () => setOpenAdd(true);

    const handleToggle = (id) => {
        const current = tasks.find((t) => t.id === id);
        if (!current) return;
        toggleMutation.mutate({ id, done: !current.done });
    };

    if (!token) {
        return (
            <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 bg-white">
                <AuthCard className="flex-1 w-full  mx-auto" title={"오늘의 할 일"} onClose={() => { }}>
                    <div className="p-6">로그인이 필요합니다. 먼저 로그인 해주세요.</div>
                </AuthCard>
                <BottomNav className="h-16" />
            </main>
        );
    }

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full  mx-auto"
                    title={"오늘의 할 일"}
                    onClose={() => navigate(-1)}
                >
                    <div className="flex flex-col flex-1 pt-8 pb-14">
                        <section
                            className="
                            flex-1 
                            pb-[calc(4rem+env(safe-area-inset-bottom))]  
                            space-y-7
                        "
                        >
                            <div>
                                <div className="flex items-center justify-between pb-2">
                                    <p className="text-2xl md:text-3xl leading-tight font-bold">
                                        오늘의 할 일 설정
                                    </p>

                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleAdd}
                                            aria-label="추가"
                                            className="
                                        flex items-center justify-center text-base md:text-lg text-gray-400
                                        "
                                        >
                                            추가
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setOpenEdit(true)}
                                            aria-label="수정"
                                            className="
                                        flex items-center justify-center text-base md:text-lg text-gray-400
                                        "
                                        >
                                            수정
                                        </button>
                                    </div>
                                </div>

                                <div
                                    className="
                                    px-7 py-4 md:px-7 py-8
                                    bg-white rounded-2xl
                                    border border-gray-200
                                    flex flex-col space-y-8
                                    "
                                >
                                    <div
                                        className="
                                        flex flex-col space-y-2
                                        items-center    
                                    "
                                    >
                                        <CircularProgress
                                            value={percentInt}
                                            label={`${percentInt}`}
                                            className="w-48 h-48 md:w-64 md:h-64"
                                            type={"today-tasks"}
                                            textsize="text-6xl md:text-8xl"
                                            strokeWidth={6.5}
                                        />

                                        <p className="text-lg md:text-xl text-gray-500">
                                            {isLoading ? "불러오는 중…" : doneText}
                                        </p>
                                        {isError && (
                                            <p className="text-sm text-[#DE4F4F]">
                                                {error?.message || "불러오기 실패"}
                                            </p>
                                        )}
                                    </div>

                                    <hr className="w-full my-4" style={{ border: "1px solid #F1F1F1" }} />

                                    <div
                                        className="
                                        px-2.5
                                        bg-white rounded-2xl
                                        flex flex-col space-y-4
                                        items-start                     
                                    "
                                    >
                                        <ul className="space-y-4 md:space-y-5 text-lg md:text-xl">
                                            {tasks.map((t) => (
                                                <li key={t.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggle(t.id)}
                                                        aria-pressed={t.done}
                                                        className="w-full flex items-center gap-5  text-left rounded-lg "
                                                    >
                                                        {t.done ? (
                                                            <CheckCircleIcon className="w-7 h-7 text-[#5F9BFF]" aria-hidden />
                                                        ) : (
                                                            <CheckCircleIcon className="w-7 h-7 text-gray-300" aria-hidden />
                                                        )}
                                                        <span className={t.done ? "font-bold line-through text-gray-400" : "font-bold"}>
                                                            {t.title}
                                                        </span>
                                                    </button>
                                                </li>
                                            ))}
                                            {!isLoading && tasks.length === 0 && (
                                                <li className="text-lg text-gray-500">할 일이 없습니다.</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </AuthCard>
            )}
            <BottomNav className="h-16" />


            <TodoCreateModal
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                pending={addMutation.isPending}
                error={addMutation.error?.message}
                onCreate={async ({ title }) => {
                    await addMutation.mutateAsync({ title });
                    setOpenAdd(false);
                }}
            />

            <TodoEditModal
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                items={tasks.map((t) => t.title)}

                pending={updateMutation.isPending || addMutation.isPending || deleteMutation.isPending}
                error={error?.message}
                onSave={async (nextItems) => {
                    const titles = nextItems.map((s) => s.trim());
                    for (let i = 0; i < tasks.length; i++) {
                        const t = tasks[i];
                        const title = titles[i] ?? "";
                        if (!title) {
                            await deleteMutation.mutateAsync(t.id);
                        } else if (title !== t.title) {
                            await updateMutation.mutateAsync({ id: t.id, title });
                        }
                    }
                    for (let i = tasks.length; i < titles.length; i++) {
                        const title = titles[i];
                        if (title) await addMutation.mutateAsync({ title });
                    }

                    await queryClient.invalidateQueries({ queryKey: ["todos"] });
                    setOpenEdit(false);
                }}
            />

        </main>
    );
}
